// Option lists and labels for the structure forms (metric definitions, targets, seat metric
// sets, seats), mirroring the contract enums (@companyos/schema `enums.ts`; the web app reads
// the schema as types only), and the one conversion between what a person types and the stored
// value: currency in major units ⇄ minor units, percent in % ⇄ a fraction.
import type { MetricDef, Seat, SeatMetric, TargetRule } from "../../api/types";
import { fractionDigits } from "../format";

type Option<T extends string> = { value: T; label: string };

export const UNIT_OPTIONS: Option<MetricDef["unit"]>[] = [
  { value: "number", label: "Number" },
  { value: "currency", label: "Currency" },
  { value: "percent", label: "Percent" },
  { value: "time", label: "Time (minutes)" },
  { value: "rating", label: "Rating" },
  { value: "yes_no", label: "Yes / no" },
  { value: "done_not_done", label: "Done / not done" },
];

export const DIRECTION_OPTIONS: Option<MetricDef["direction"]>[] = [
  { value: "gte", label: "At least (≥)" },
  { value: "gt", label: "More than (>)" },
  { value: "lte", label: "At most (≤)" },
  { value: "lt", label: "Less than (<)" },
  { value: "eq", label: "Exactly (=)" },
  { value: "between", label: "Between (range)" },
];

export const AGGREGATION_OPTIONS: Option<MetricDef["aggregation"]>[] = [
  { value: "cumulative", label: "Add up over the period" },
  { value: "average", label: "Average over the period" },
];

export const CADENCE_OPTIONS: Option<NonNullable<MetricDef["cadence"]>>[] = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "yearly", label: "Yearly" },
];

/** KPI-ASSIGN: "From events" opens the event-metric builder (features/kpi/event-query.tsx). */
export const SOURCE_OPTIONS: Option<MetricDef["source"]>[] = [
  { value: "manual", label: "Entered by hand" },
  { value: "import", label: "Imported (CSV)" },
  { value: "event", label: "From events (agents, ingest)" },
  { value: "formula", label: "Formula over other metrics" },
  { value: "task", label: "Counted from a board's tasks" },
];

export const SOURCE_LABELS: Record<MetricDef["source"], string> = {
  manual: "Manual",
  import: "Import",
  event: "Events",
  formula: "Formula",
  ledger: "Cost ledger",
  task: "Board tasks",
};

export const GAP_OPTIONS: Option<MetricDef["gap_policy"]>[] = [
  { value: "excuse", label: "A missing value is excused" },
  { value: "count", label: "A missing value counts as zero" },
];

export const TRACKING_OPTIONS: Option<SeatMetric["tracking_mode"]>[] = [
  { value: "scored", label: "Scored" },
  { value: "context", label: "Context only" },
  { value: "off", label: "Off" },
];

export const PERIOD_OPTIONS: Option<Exclude<TargetRule["period_type"], "round">>[] = [
  { value: "day", label: "Per day" },
  { value: "week", label: "Per week" },
  { value: "month", label: "Per month" },
  { value: "quarter", label: "Per quarter" },
  { value: "year", label: "Per year" },
];

export const SEAT_STATUS_LABELS: Record<Seat["status"], string> = {
  proposed: "Proposed",
  open: "Open",
  filled: "Filled",
  paused: "Paused",
  closed: "Closed",
};

/** Statuses a seat edit may set (closing has its own action; `proposed` is never written). */
export const SEAT_STATUS_OPTIONS: Option<"open" | "filled" | "paused">[] = [
  { value: "open", label: "Open" },
  { value: "filled", label: "Filled" },
  { value: "paused", label: "Paused" },
];

export const labelOf = <T extends string>(options: Option<T>[], value: T | null | undefined) =>
  options.find((o) => o.value === value)?.label ?? value ?? "—";

/** What a person types → the stored decimal (null when it is not a number). */
export function toStoredValue(
  input: string,
  unit: MetricDef["unit"],
  currency: string,
): number | null {
  const v = Number(input);
  if (input.trim() === "" || !Number.isFinite(v)) return null;
  if (unit === "currency") return Math.round(v * 10 ** fractionDigits(currency));
  if (unit === "percent") return Math.round((v / 100) * 1e6) / 1e6;
  return v;
}

/** A stored decimal → what the form shows (the inverse of `toStoredValue`). */
export function toInputValue(stored: string | number, unit: MetricDef["unit"], currency: string) {
  const v = Number(stored);
  if (!Number.isFinite(v)) return "";
  if (unit === "currency") return String(v / 10 ** fractionDigits(currency));
  if (unit === "percent") return String(Math.round(v * 100 * 1e4) / 1e4);
  return String(v);
}

export const UNIT_HINT: Partial<Record<MetricDef["unit"], string>> = {
  currency: "In the company's currency (e.g. 5000 for $5,000).",
  percent: "In percent (e.g. 35 for 35 %).",
  time: "In minutes.",
};
